import React from 'react';
import PropTypes from 'prop-types';
import { copenhagenParts, formatCopenhagen } from '../../common/copenhagenTime';

/**
 * Pieces shared by the device job list and the device job detail.
 *
 * The statuses are DeviceJobStatus on the backend. Their tones are kept here and
 * not added to the shared StatusPill map on purpose: "Unknown" there would also
 * recolour every other page that happens to print an unknown status, while here
 * it has one precise and alarming meaning — a runner took the job and never said
 * what happened, so the booking may have been made on the device.
 */

export const STATUSES = ['Queued', 'Claimed', 'Succeeded', 'Failed', 'Expired', 'Unknown'];

// The ones somebody may have to act on, in the order they are shown.
export const ATTENTION = ['Unknown', 'Queued', 'Claimed'];

const TONE = {
  Queued: 'info',
  Claimed: 'blue',
  Succeeded: 'ok',
  Failed: 'bad',
  Expired: 'warn',
  Unknown: 'bad'
};

export const STATUS_HINT = {
  Queued: 'Waiting for a runner to take it.',
  Claimed: 'A runner took it: the script is running on the device.',
  Succeeded: 'The script reported success.',
  Failed: 'The script reported a failure. Nothing was booked.',
  Expired: 'No runner took it in time: the script never ran.',
  Unknown: 'A runner took it and sent no result in time: the booking may have happened on the device. Check the device before retrying.'
};

export function JobStatus({ status }) {
  return (
    <span className={`next-pill next-pill--${TONE[status] || 'neutral'}`} title={STATUS_HINT[status]}>
      {status === 'Unknown' ? '⚠ Unknown' : (status || '—')}
    </span>
  );
}

JobStatus.propTypes = {
  status: PropTypes.string
};

// Backend timestamps carry an offset (UTC): converted to Copenhagen time.
export const cph = value => formatCopenhagen(value, { fallback: '—' });

const seconds = (from, to) => {
  if (!from || !to) return null;
  const ms = new Date(to).getTime() - new Date(from).getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms / 1000 : null;
};

export const duration = (from, to) => {
  const s = seconds(from, to);
  if (s === null) return null;
  if (s < 120) return `${Math.round(s)} s`;
  if (s < 7200) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
};

/**
 * Midnight of a Copenhagen day as an instant with its offset, for the From/To
 * filters: "2026-11-04" → "2026-11-04T00:00:00+01:00".
 */
export const copenhagenMidnight = day => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return undefined;
  const zone = copenhagenParts(`${day}T12:00:00Z`)?.zone;
  return `${day}T00:00:00${zone === 'CEST' ? '+02:00' : '+01:00'}`;
};

export const nextDay = day => {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};
